import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Select, Input, InputNumber, Button, Row, Col, Spinner, Tag, message } from '../../ui';
import { Plus, Trash2 } from 'lucide-react';
import { request } from '../../api/client';
import { inr2 } from '../../lib/format';

const ITEM_CATEGORIES = ['TRAVEL', 'FOOD', 'LOCAL_CONVEYANCE', 'HOTEL', 'FUEL', 'TOLL_PARKING', 'TRAIN', 'FLIGHT', 'BUS', 'OTHER'];
const DESC_REQUIRED = new Set(['HOTEL', 'OTHER']);
// Two vocabularies share the `visit_type` column: a report filed off a VISIT freezes that visit's type
// (RETAILER / FARMER / PROSPECT), while a standalone report picks its own. Both are offered, grouped, so
// editing a visit-linked report never silently rewrites its snapshot to an unrelated value.
const VISIT_TYPE_GROUPS = [
  { label: 'From a visit', values: ['RETAILER', 'FARMER', 'PROSPECT'] },
  { label: 'Standalone', values: ['FIELD_VISIT', 'TOUR', 'OFFICE', 'OTHER'] },
];
const TRAVEL_MODE_OPTIONS = ['CAR', 'BIKE', 'TRAIN', 'FLIGHT', 'BUS', 'TAXI', 'OTHER'].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));
const titleize = (v: string) => v.replace(/_/g, ' ');

/** Column track shared by the expense header row, every line and the total — so all three stay aligned. */
const LINE_GRID = 'grid grid-cols-[minmax(0,9.5rem)_minmax(0,8rem)_minmax(0,1fr)_2rem] items-center gap-2';

const FieldLabel = ({ children, required }: { children: any; required?: boolean }) => (
  <label className="mb-1 block text-sm text-muted">
    {children}
    {required && <span className="ml-0.5 text-danger-500">*</span>}
  </label>
);

function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-line pb-2">
      <h3 className="type-body-strong text-content">{title}</h3>
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </div>
  );
}

type LineRow = { key: string; id?: string; category: string; description?: string; amount?: number; vendorName?: string; orig?: any };
export type EditTab = 'expenses' | 'details';

// Self-contained draft editor — edits a report's header + line items in one place. Used both
// inline from the Expense Reports table and from the report detail page, so editing never has
// to leave the current screen. The server re-guards (owner/filer + draft/sent-back) on save.
export function EditReportModal({ reportId, initialTab = 'expenses', onClose, onSaved }: {
  reportId: string; initialTab?: EditTab; onClose: () => void; onSaved: () => void;
}) {
  const { data: report, isLoading } = useQuery({
    queryKey: ['expense-report', reportId],
    queryFn: () => request<any>({ url: `/expense-reports/${reportId}` }),
    refetchOnMount: 'always',
  });
  if (isLoading || !report) {
    return (
      <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Edit report">
        <div style={{ textAlign: 'center', padding: 32 }}><Spinner /></div>
      </Modal>
    );
  }
  return <EditReportBody report={report} reportId={reportId} initialTab={initialTab} onClose={onClose} onSaved={onSaved} />;
}

function EditReportBody({ report, reportId, initialTab, onClose, onSaved }: {
  report: any; reportId: string; initialTab: EditTab; onClose: () => void; onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  // Set on the first save attempt — until then, empty fields stay neutral instead of shouting red.
  const [showErrors, setShowErrors] = useState(false);
  // A claim filed off a visit carries that visit's party + type as frozen snapshots — not editable here.
  const visitLinked = !!report.visitId;
  const detailsRef = useRef<HTMLDivElement>(null);
  const expensesRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<Record<string, any>>({
    visitType: report.visitType ?? undefined,
    travelMode: report.travelMode ?? undefined,
    fundingSource: report.fundingSource ?? 'WALLET',
    partyName: report.partyName ?? '',
    partyContact: report.partyContact ?? '',
    partyAddress: report.partyAddress ?? '',
    city: report.city ?? '',
    state: report.state ?? '',
    pincode: report.pincode ?? '',
    workDone: report.workDone ?? '',
    notes: report.notes ?? '',
  });
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  /*
   * WHICH POT PAYS. Two, and they behave completely differently:
   *
   *   WALLET           company float already released to this person. Approving the claim draws it
   *                    down, and any shortfall is reimbursed at settlement.
   *   COLLECTION MONEY retailer cash they are holding and have not yet banked. The money already left
   *                    their hands, so nothing is reimbursed \u2014 approving simply stops them being
   *                    accountable for that much.
   *
   * The collection option is only offered when they actually have some: an option that always fails on
   * save is worse than one that is not there.
   */
  const collectionMoney = useQuery({
    queryKey: ['staff-retailer-money', report.repId],
    queryFn: () => request<{ outstanding: number; awaitingConfirmation: number }>({ url: `/retailer-money/staff/${report.repId}` }),
    enabled: !!report.repId,
    retry: false,
  });
  /*
   * SPENDABLE = what is still in the bag, which is not the same as what they are accountable for.
   *
   * Cash already recorded as banked and waiting on Accounts is either in a company account or was never
   * there. Offering it as a funding source would fund a claim out of money the rep does not hold, and
   * the moment the deposit confirmed their position would go negative. The server refuses on exactly
   * this figure, so the picker has to show the same one or it offers a choice that always fails.
   */
  const collectionAvailable = Math.max(0, Math.round((
    Number(collectionMoney.data?.outstanding ?? 0) - Number(collectionMoney.data?.awaitingConfirmation ?? 0)
  ) * 100) / 100);

  const [rows, setRows] = useState<LineRow[]>(() =>
    (report.items ?? []).map((i: any) => ({
      key: `e${i.id}`, id: String(i.id), category: i.category, description: i.description ?? '', amount: Number(i.amount), vendorName: i.vendorName ?? '',
      orig: { category: i.category, description: i.description ?? '', amount: Number(i.amount), vendorName: i.vendorName ?? '' },
    })),
  );
  const [removed, setRemoved] = useState<string[]>([]);
  const upd = (key: string, patch: Partial<LineRow>) => setRows((a) => a.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((a) => [...a, { key: `n${a.length}-${Math.random().toString(36).slice(2)}`, category: 'TRAVEL' }]);
  const removeRow = (r: LineRow) => { if (r.id) setRemoved((d) => [...d, r.id!]); setRows((a) => a.filter((x) => x.key !== r.key)); };

  // The claim total, live — an expense report's headline number should never need mental arithmetic.
  const total = useMemo(() => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0), [rows]);

  // Whichever section the caller asked for ("Edit expenses" vs "Edit details") is scrolled to on open —
  // both are on one page now, so this replaces the old tab switch.
  useEffect(() => {
    if (initialTab !== 'details') return;
    detailsRef.current?.scrollIntoView({ block: 'start' });
  }, [initialTab]);

  const lineError = (r: LineRow) => {
    if (r.amount == null || Number(r.amount) <= 0) return 'amount';
    if (!r.category) return 'category';
    if (DESC_REQUIRED.has(r.category) && !r.description?.trim()) return 'description';
    return null;
  };

  // One Save: PATCH the header, then reconcile line items (delete removed → patch changed → post new).
  const save = async () => {
    setShowErrors(true);
    if (!form.visitType) {
      message.error('Pick a visit type');
      detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const bad = rows.find((r) => lineError(r));
    if (bad) {
      const kind = lineError(bad);
      message.error(kind === 'description'
        ? `${titleize(bad.category)} expenses need a description`
        : kind === 'category' ? 'Every expense needs a category' : 'Every expense needs an amount greater than zero');
      expensesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setBusy(true);
    try {
      await request({ url: `/expense-reports/${reportId}`, method: 'PATCH', data: form });
      for (const delId of removed) await request({ url: `/expense-reports/${reportId}/items/${delId}`, method: 'DELETE' });
      const toUpdate = rows.filter((r) => r.id && r.orig &&
        (r.category !== r.orig.category || (r.description ?? '') !== (r.orig.description ?? '') || Number(r.amount) !== r.orig.amount || (r.vendorName ?? '') !== (r.orig.vendorName ?? '')));
      for (const u of toUpdate) {
        await request({ url: `/expense-reports/${reportId}/items/${u.id}`, method: 'PATCH', data: { category: u.category, description: u.description || undefined, amount: Number(u.amount), vendorName: u.vendorName || undefined } });
      }
      const toCreate = rows.filter((r) => !r.id);
      if (toCreate.length) {
        await request({ url: `/expense-reports/${reportId}/items`, method: 'POST', data: { items: toCreate.map((r) => ({ category: r.category, description: r.description || undefined, amount: Number(r.amount), vendorName: r.vendorName || undefined })) } });
      }
      message.success('Report updated');
      onSaved(); onClose();
    } catch (e: any) { message.error(e?.response?.data?.error?.message ?? 'Failed'); } finally { setBusy(false); }
  };

  // Visit type: the canonical groups, plus the report's own value if it predates them (never drop it).
  const visitTypeOptions = useMemo(() => {
    const known = new Set(VISIT_TYPE_GROUPS.flatMap((g) => g.values));
    const groups: any[] = VISIT_TYPE_GROUPS.map((g) => ({ label: g.label, options: g.values.map((v) => ({ value: v, label: titleize(v) })) }));
    if (form.visitType && !known.has(form.visitType)) {
      groups.unshift({ label: 'Current', options: [{ value: form.visitType, label: titleize(String(form.visitType)) }] });
    }
    return groups;
  }, [form.visitType]);

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Edit ${report.reportNo}`}
      description={[titleize(String(report.status ?? '')), report.partyName].filter(Boolean).join(' · ')}
      width={780}
      footer={(
        <>
          <span className="mr-auto text-sm text-muted">
            {rows.length} {rows.length === 1 ? 'expense' : 'expenses'} · <span className="font-semibold text-content">{inr2(total)}</span>
          </span>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={save}>Save</Button>
        </>
      )}
    >
      <div className="space-y-6">
        {/* ── Expense lines ────────────────────────────────────────────────────────────── */}
        <section ref={expensesRef}>
          <SectionHeading title="Expenses" hint="What this visit cost" />
          {rows.length === 0 ? (
            <div className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-muted">
              No expense lines yet.
            </div>
          ) : (
            <>
              <div className={`${LINE_GRID} px-1 pb-1 type-overline text-subtle`}>
                <span>Category</span>
                <span className="text-right">Amount</span>
                <span>Description</span>
                <span />
              </div>
              <div className="divide-y divide-line/70">
                {rows.map((r) => {
                  const err = showErrors ? lineError(r) : null;
                  const descRequired = DESC_REQUIRED.has(r.category);
                  return (
                    <div key={r.key} className={`${LINE_GRID} px-1 py-2`}>
                      <Select
                        value={r.category} onChange={(v) => upd(r.key, { category: v })} className="w-full"
                        options={ITEM_CATEGORIES.map((c) => ({ value: c, label: titleize(c) }))}
                      />
                      <InputNumber
                        min={1} placeholder="0.00" value={r.amount} prefix="₹" invalid={err === 'amount'}
                        style={{ textAlign: 'right' }}
                        onChange={(v) => upd(r.key, { amount: v == null ? undefined : Number(v) })}
                      />
                      <Input
                        placeholder={descRequired ? 'Description (required)' : 'Description (optional)'}
                        value={r.description} invalid={err === 'description'}
                        onChange={(e) => upd(r.key, { description: e.target.value })}
                      />
                      <Button
                        variant="text" size="sm" aria-label="Remove expense" title="Remove expense"
                        className="text-muted hover:bg-danger-500/10 hover:text-danger-600"
                        icon={<Trash2 size={16} />} onClick={() => removeRow(r)}
                      />
                    </div>
                  );
                })}
              </div>
              <div className={`${LINE_GRID} border-t border-line px-1 pt-2`}>
                <span className="text-right type-body-medium text-muted">Total</span>
                <span className="pr-3 text-right type-body-strong text-content">{inr2(total)}</span>
                <span />
                <span />
              </div>
            </>
          )}
          {/* Neutral + dashed: adding a line is a secondary affordance, and a second green button
              would compete with Save for the eye. */}
          <Button variant="text" block className="mt-3 border border-dashed border-line text-muted hover:border-brand-400 hover:text-content"
            icon={<Plus size={16} />} onClick={addRow}>
            Add line
          </Button>
        </section>

        {/* ── Visit details ──────────────────────────────────────────────────────────────
            Only for a STANDALONE claim. When the claim is linked to a visit, these fields are
            frozen snapshots the visit owns — editing them here would contradict the link — so the
            visit is shown as read-only context instead. (Expenses are moving to a per-FIELD-DAY
            claim, at which point the party belongs on the line, not the header, and this whole
            section goes away.) */}
        {visitLinked ? (
          <section ref={detailsRef}>
            <SectionHeading title="Visit" hint="Frozen from the linked visit" />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
              {report.partyName && <span className="font-medium text-content">{report.partyName}</span>}
              {form.visitType && <Tag tone="blue">{titleize(String(form.visitType))}</Tag>}
              {[report.city, report.state].filter(Boolean).length > 0 && (
                <span className="text-muted">{[report.city, report.state].filter(Boolean).join(' · ')}</span>
              )}
              {report.partyContact && <span className="text-muted">{report.partyContact}</span>}
            </div>
          </section>
        ) : (
        <section ref={detailsRef}>
          <SectionHeading title="Visit details" hint="Who was visited, and where" />
          <Row gutter={[12, 12]}>
            <Col span={12}><FieldLabel required>Visit type</FieldLabel>
              <Select className="w-full" placeholder="Visit type" value={form.visitType} onChange={(v) => set('visitType', v)}
                invalid={showErrors && !form.visitType} options={visitTypeOptions} /></Col>
            <Col span={12}><FieldLabel>Travel mode</FieldLabel>
              <Select className="w-full" placeholder="Travel mode" allowClear value={form.travelMode || undefined} onChange={(v) => set('travelMode', v ?? '')} options={TRAVEL_MODE_OPTIONS} /></Col>
            <Col span={24}><FieldLabel>Paid for with</FieldLabel>
              <Select
                className="w-full"
                value={form.fundingSource ?? 'WALLET'}
                onChange={(v) => set('fundingSource', v)}
                options={[
                  { value: 'WALLET', label: 'Company wallet / float' },
                  {
                    value: 'CASH_CUSTODY',
                    label: collectionAvailable > 0.005
                      ? `Collection money \u00b7 ${inr2(collectionAvailable)} available`
                      : 'Collection money \u2014 none held',
                    disabled: collectionAvailable <= 0.005,
                  },
                ]}
              />
              <div className="mt-1 type-caption text-subtle">
                {form.fundingSource === 'CASH_CUSTODY'
                  ? 'Paid out of retailer cash you are holding. Nothing is reimbursed \u2014 approving this reduces what you still owe the company by the approved amount.'
                  : 'Drawn from your company float. Anything over your float is reimbursed to you when the claim is settled.'}
              </div>
              {/*
                * Say it here, not at the approver's desk.
                *
                * One pot pays a claim: cash-funded means the whole total comes out of collected cash,
                * so a claim larger than the cash in hand is refused on submit. Showing the shortfall
                * while the total is still being typed is the difference between a five-second fix and
                * a rejection three days later.
                */}
              {form.fundingSource === 'CASH_CUSTODY' && total > collectionAvailable + 0.005 && (
                <div className="mt-1 rounded-control bg-warning-500/10 px-2 py-1 type-caption text-warning-600 dark:text-harvest-300">
                  {inr2(total)} is more than the {inr2(collectionAvailable)} of collected cash you are holding.
                  Pay for it from the wallet, or split the claim so the cash-funded part fits.
                </div>
              )}
            </Col>
            <Col span={12}><FieldLabel>Party name</FieldLabel>
              <Input value={form.partyName} onChange={(e) => set('partyName', e.target.value)} placeholder="Customer / party" /></Col>
            <Col span={12}><FieldLabel>Party contact</FieldLabel>
              <Input value={form.partyContact} onChange={(e) => set('partyContact', e.target.value)} placeholder="Phone" /></Col>
            <Col span={24}><FieldLabel>Address</FieldLabel>
              <Input value={form.partyAddress} onChange={(e) => set('partyAddress', e.target.value)} placeholder="Site address" /></Col>
            <Col span={10}><FieldLabel>City</FieldLabel>
              <Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Col>
            <Col span={8}><FieldLabel>State</FieldLabel>
              <Input value={form.state} onChange={(e) => set('state', e.target.value)} /></Col>
            <Col span={6}><FieldLabel>Pincode</FieldLabel>
              <Input value={form.pincode} onChange={(e) => set('pincode', e.target.value)} /></Col>
          </Row>
        </section>
        )}

        {/* ── Narrative ────────────────────────────────────────────────────────────────── */}
        <section>
          <SectionHeading title="Notes" hint="Optional" />
          <Row gutter={[12, 12]}>
            <Col span={24}><FieldLabel>Work done</FieldLabel>
              <Input.TextArea rows={2} value={form.workDone} onChange={(e) => set('workDone', e.target.value)} placeholder="What was done on this visit" /></Col>
            <Col span={24}><FieldLabel>Internal notes</FieldLabel>
              <Input.TextArea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Anything the approver should know" /></Col>
          </Row>
        </section>
      </div>
    </Modal>
  );
}
